import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import { stayExtensionText } from '../i18n/pages/stayExtension';
import {
  StayExtensionQuote,
  getStayExtensionQuote,
  isArrival,
  startStayExtensionCheckout,
} from '../api.stayExtension';
import './PublicStayExtensionPay.css';

/**
 * Paying for approved extra hours, on a page of its own.
 *
 * <p>Separate from the check-in guide on purpose. A manager may want to send this link on its own —
 * to whoever is actually paying, through whatever channel the guest booked on — and the guide
 * carries the door code. Two purposes, two tokens, so forwarding one never hands over the other.
 *
 * <p>Every unusable state (expired, declined, withdrawn, already paid, never existed) comes back
 * from the API as the same 404 and is rendered as the same message here. Telling the visitor which
 * kind of invalid a token is would turn the page into an oracle for guessing at live ones.
 *
 * <p>The three states share one frame — header, card, footer — because a stranger arriving at a
 * page that is about to ask for their card needs it to look like it belongs to someone. A bare
 * centred card with no letterhead is indistinguishable from a phishing page.
 */
const PublicStayExtensionPay: React.FC = () => {
  const { paymentToken } = useParams<{ paymentToken: string }>();
  const [searchParams] = useSearchParams();
  const { lang } = useLang();
  const t = stayExtensionText[lang];

  const [quote, setQuote] = useState<StayExtensionQuote | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const justPaid = searchParams.get('payment') === 'success';

  const load = useCallback(async () => {
    if (!paymentToken) return;
    setLoading(true);
    try {
      setQuote(await getStayExtensionQuote(paymentToken));
    } catch {
      setQuote(null);
    } finally {
      setLoading(false);
    }
  }, [paymentToken]);

  useEffect(() => { void load(); }, [load]);

  const pay = async () => {
    if (!paymentToken) return;
    setStarting(true);
    setError(null);
    try {
      window.location.href = await startStayExtensionCheckout(paymentToken);
    } catch (e: any) {
      setError(e?.response?.data?.message || t.payError);
      setStarting(false);
    }
  };

  const fill = (template: string, values: Record<string, string>) =>
    Object.entries(values).reduce((s, [k, v]) => s.replace(`{${k}}`, v), template);

  const locale = lang === 'en' ? 'en-GB' : lang;
  /** ISO dates from the API; shown the way the reader's own language writes them. */
  const date = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  /** The header names the apartment once it is known, so the page has a sender from the first line. */
  const Shell: React.FC<{ subject?: string; children: React.ReactNode }> = ({ subject, children }) => (
    <div className="sxp">
      <header className="sxp-head">
        <div className="sxp-head-in">
          {/* The same mark the rest of the product uses, rather than a stand-in: this page's whole
              job in its first second is to look like it came from somewhere real. */}
          <span className="sxp-mark" aria-hidden="true">
            <svg
              width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
          </span>
          <span className="sxp-brand">
            <b>{subject || 'Guestbook'}</b>
            <span>{t.payHeaderTag}</span>
          </span>
        </div>
      </header>

      <main className="sxp-body">{children}</main>

      <footer className="sxp-foot">
        <span className="lock">🔒 {t.paySecure}</span>
      </footer>
    </div>
  );

  if (loading) {
    return (
      <Shell>
        <div className="sxp-card sxp-centred">
          <p className="sxp-lead">{t.payLoading}</p>
        </div>
      </Shell>
    );
  }

  // Coming back from a successful Stripe redirect, the quote is already PAID and therefore no
  // longer payable — so the "invalid link" branch below would otherwise greet someone who just
  // succeeded. Check the redirect flag first.
  if (justPaid) {
    return (
      <Shell>
        <div className="sxp-card sxp-centred">
          <div className="sxp-badge ok" aria-hidden="true">✓</div>
          <h1 className="sxp-title">{t.payDone}</h1>
          <p className="sxp-lead">{t.payDoneBody}</p>
        </div>
      </Shell>
    );
  }

  if (!quote) {
    return (
      <Shell>
        <div className="sxp-card sxp-centred">
          <div className="sxp-badge dead" aria-hidden="true">·</div>
          <h1 className="sxp-title">{t.payInvalidTitle}</h1>
          <p className="sxp-lead">{t.payInvalidBody}</p>
        </div>
      </Shell>
    );
  }

  // This page is the last thing a guest reads before their card is charged, so it has to name the
  // right product. Calling a bag drop-off an "early check-in" here would take money for something
  // the guest did not agree to buy.
  const isEarly = isArrival(quote.type);
  const eyebrow = quote.type === 'EARLY_CHECKIN' ? t.earlyTitle
    : quote.type === 'LATE_CHECKOUT' ? t.lateTitle
    : quote.type === 'LUGGAGE_DROPOFF' ? t.luggageDropoffTitle
    : t.luggagePickupTitle;
  const title = quote.type === 'EARLY_CHECKIN' ? t.payTitleEarly
    : quote.type === 'LATE_CHECKOUT' ? t.payTitleLate
    : quote.type === 'LUGGAGE_DROPOFF' ? t.payTitleLuggageIn
    : t.payTitleLuggageOut;
  const caption = quote.type === 'EARLY_CHECKIN' ? t.payHoursInLabel
    : quote.type === 'LATE_CHECKOUT' ? t.payHoursOutLabel
    : quote.type === 'LUGGAGE_DROPOFF' ? t.payLuggageInLabel
    : t.payLuggageOutLabel;
  const price = `${quote.price.toLocaleString(locale)} ${quote.currency}`;

  return (
    <Shell subject={quote.apartmentName}>
      <div className="sxp-card">
        <p className="sxp-eyebrow">{eyebrow}</p>
        <h1 className="sxp-title">
          {fill(title, { time: quote.grantedTime })}
        </h1>

        {/* The hours, at the size the question deserves. The standard time is struck through beside
            them because "what am I actually gaining" is answered by the pair, not by either alone.
            For luggage the pair reads differently but no less usefully: the hour the flat opens,
            and the hour the bags may arrive — which is exactly what is being bought. */}
        <div className="sxp-hours">
          <span className="was">{quote.standardTime}</span>
          <span className="arr" aria-hidden="true">→</span>
          <span className="now">{quote.grantedTime}</span>
          <span className="cap">{caption}</span>
        </div>

        <div className="sxp-rows">
          <div className="sxp-row">
            <span className="k">{t.payRowApartment}</span>
            <span className="v">{quote.apartmentName}</span>
          </div>
          <div className="sxp-row">
            <span className="k">{t.payRowDates}</span>
            <span className="v">{date(quote.checkInDate)} – {date(quote.checkOutDate)}</span>
          </div>
          <div className="sxp-row total">
            <span className="k">{t.payTotal}</span>
            <span className="v">{price}</span>
          </div>
        </div>

        {quote.quoteExpiresAt && (
          <div className="sxp-note">
            <span aria-hidden="true">⏳</span>
            <span>{fill(t.quoteUntil, { date: date(quote.quoteExpiresAt) })}</span>
          </div>
        )}

        <button type="button" onClick={pay} disabled={starting} className="sxp-cta">
          {starting ? t.payLoading : fill(t.payCta, { price })}
        </button>

        <p className="sxp-reassure">{t.payNothingChanges}</p>
        {error && <div className="sxp-error">{error}</div>}
      </div>
    </Shell>
  );
};

export default PublicStayExtensionPay;
